import { LegalPageLayout, Section } from "../components/legal/LegalPageLayout";

function DateCallout({ date, text }: { date: string; text: string }) {
  return (
    <div
      className="my-4 rounded-lg border-l-4 p-4"
      style={{
        borderColor: "var(--color-primary)",
        background: "var(--color-surface-secondary)",
      }}
    >
      <p className="font-semibold text-[var(--color-text-primary)]">{date}</p>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{text}</p>
    </div>
  );
}

export function Blog() {
  return (
    <LegalPageLayout
      title="Von AFID bis AFIR: Die Regulierungsgeschichte der Ladeinfrastruktur in Deutschland"
      lastUpdated="22. April 2026"
      crossLink={{ to: "/", label: "Zurueck zur Startseite" }}
      contactEmail="contact@chargesuite.de"
    >
      {/* 1 — Einleitung */}
      <Section
        id="einfuehrung"
        title="Einleitung: Warum Regulierung jetzt zwingend ist"
      >
        <p>
          Wer heute in Deutschland eine oeffentliche Ladestation betreibt, steht
          vor sieben gleichzeitig geltenden Regelwerken: AFIR, Eichrecht, LSV,
          EnWG, BDEW-Richtlinien, Cyber Resilience Act und DSGVO. Jedes
          Regelwerk hat eigene Fristen, eigene Bussgeldrahmen und eigene
          Zustaendigkeitsbehoerden. Zusammen ergeben sie ueber 120
          Einzelpflichten, die ein Charge Point Operator (CPO) gleichzeitig
          beherrschen muss.
        </p>
        <p>
          Diese Komplexitaet ist nicht zufaellig. Sie spiegelt einen
          Jahrzehnte langen politischen Prozess wider, der auf EU-Ebene mit
          der AFID von 2014 begann und bis heute nicht abgeschlossen ist. Wer
          die Regulierungsgeschichte versteht, kann Fristen antizipieren statt
          sie zu verpassen, Compliance-Luecken schliessen bevor Behoerden sie
          finden, und Investitionen in Infrastruktur langfristig planen.
        </p>
        <p>
          Dieser Artikel rekonstruiert die wichtigsten Stationen dieser
          Regulierungsgeschichte chronologisch — von der EU-Mutterdirektive
          2014 bis zu den Dichtezielen 2030. Jede Etappe wird mit den
          konkreten Rechtsnormen verankert, die heute noch gelten oder in
          Kuerze in Kraft treten. Grundlage sind sieben Verordnungen, deren
          Zusammenspiel und gelegentliche Widerspruechlichkeit (etwa zwischen
          AFIR-Zeitpreiskomponenten und der Eichrecht-Pflicht zur
          kWh-Primaerbasierung) Betreiber tagtaeglich herausfordert.
        </p>
        <p>
          Die Behoerden — Bundesnetzagentur, PTB, BSI, ENISA — erwarten keine
          Rechtskenntnis aus dem Stegreif. Sie erwarten aber nachgewiesene
          Konformitaet. Wer diese nicht belegen kann, riskiert Bussgelder, die
          pro Ladepunkt und pro Verstoss angesetzt werden und sich bei groesseren
          Flotten schnell zu existenzbedrohenden Summen addieren.
        </p>
      </Section>

      {/* 2 — AFID 2014 */}
      <Section
        id="afid-2014"
        title="2014: AFID — die EU-Mutterdirektive (Richtlinie 2014/94/EU)"
      >
        <p>
          Am 22. Oktober 2014 trat die Richtlinie 2014/94/EU ueber den Aufbau
          der Infrastruktur fuer alternative Kraftstoffe in Kraft — kurz{" "}
          <strong>AFID</strong> (Alternative Fuels Infrastructure Directive).
          Sie bildete den ersten verbindlichen EU-Rahmen fuer Ladeinfrastruktur
          und verpflichtete die Mitgliedstaaten, nationale Strategierahmen zu
          verabschieden sowie Mindestanforderungen an technische Spezifikationen
          und Nutzerschnittstellen festzulegen.
        </p>
        <p>
          Die AFID war bewusst als Richtlinie konzipiert: Sie setzte Ziele,
          ueberliess die konkrete Umsetzung aber den nationalen Gesetzgebern.
          Das fuehrte zu erheblichen Unterschieden zwischen den
          Mitgliedstaaten. Deutschland setzte die AFID primaer ueber die
          Ladesaeulenverordnung (LSV) um, die 2016 erstmals in Kraft trat.
          Frankreich, die Niederlande und skandinavische Laender wahlten teils
          abweichende Umsetzungspfade.
        </p>
        <p>
          Zentrales Instrument der AFID war Art. 4 Abs. 1, der technische
          Mindeststandards fuer Steckverbinder vorschrieb und damit erstmals
          eine EU-weite Steckerharmonisierung anstrebte. Der Typ-2-Stecker
          (IEC 62196-2) fuer AC und das Combined Charging System (CCS) fuer DC
          wurden de facto etabliert — eine Vorentscheidung, die die spaetere
          LSV in Deutschland aufgriff.
        </p>
        <p>
          Die AFID enthielt noch keine Zahlungspflichten und keine
          Bussgeldrahmen. Sie war ein Rahmenwerk, das auf freiwillige
          Marktentwicklung setzte. Diese Zurueckhaltung erwies sich als
          Schwaeche: Bis 2020 war das europaische Ladenetz fragmentiert,
          interoperabel mangelhaft und die Zahlungserfahrung fuer Endnutzer
          oft unzumutbar. Diese Defizite motivierten die Kommission, von einer
          Richtlinie zu einer unmittelbar geltenden{" "}
          <strong>Verordnung</strong> zu wechseln — der AFIR.
        </p>

        <DateCallout
          date="22. Oktober 2014"
          text="Richtlinie 2014/94/EU (AFID) tritt in Kraft — erster verbindlicher EU-Rahmen fuer Ladeinfrastruktur."
        />
      </Section>

      {/* 3 — LSV und Eichrecht */}
      <Section
        id="lsv-eichrecht"
        title="2016-2018: LSV und Eichrecht schaffen nationale Pflichten"
      >
        <p>
          Deutschland reagierte auf die AFID schneller als die meisten
          Mitgliedstaaten. Die{" "}
          <strong>Ladesaeulenverordnung (LSV)</strong> trat am 17. Maerz 2016
          erstmals in Kraft und schuf damit das erste nationale Regelwerk
          speziell fuer oeffentliche Ladeinfrastruktur. Gleichzeitig entstand
          mit dem{" "}
          <strong>Messstellenbetriebsgesetz (MessEG)</strong> und der{" "}
          <strong>Messstellen-betriebsverordnung (MessEV)</strong> ein
          eichrechtlicher Rahmen, der Ladeinfrastruktur erstmals als
          Messmittel im Sinne des Eichrechts behandelte.
        </p>
        <p>
          Die LSV legte in <strong>§ 3 Abs. 1 LSV</strong> die
          Steckerstandards fest: Typ-2-Stecker (IEC 62196-2) fuer AC-Ladung
          bis 22 kW, CCS Combo 2 fuer DC-Schnelllader. Sie verpflichtete CPOs
          zur Registrierung bei der Bundesnetzagentur (BNetzA) mindestens vier
          Wochen vor Inbetriebnahme, zur Vor-Ort-Kennzeichnung mit
          Betreiberdaten und Notfallkontakt sowie zur jaehrlichen
          Berichterstattung nach <strong>§ 7 LSV</strong>. Bussgelder fuer
          Verstoesse wurden in <strong>§ 8 LSV</strong> auf bis zu 100.000 EUR
          pro Verstos festgesetzt.
        </p>
        <p>
          Das Eichrecht griff parallel: Das{" "}
          <strong>Messgeraetegesetz (MessEG)</strong> definierte in{" "}
          <strong>§ 3 MessEG</strong> Anforderungen an geeichte Messmittel,
          die auch fuer Ladeinfrastruktur gelten. PTB-Typzulassungen wurden
          Pflicht. Die MessEV schrieb in <strong>Section 3 MessEV</strong> vor,
          dass Signaturen aus dem sicheren Messelement stammen muessen — nicht
          aus dem Backend. In <strong>Section 4 MessEV</strong> wurde das
          Anzeigeintervall auf zehn Sekunden festgelegt, mit Mindestaufloesung
          0,1 kWh.
        </p>
        <p>
          Die Kombination von LSV und Eichrecht schuf ein deutsches
          Regulierungsmodell, das international seinesgleichen suchte. Keine
          andere EU-Volkswirtschaft hatte zu diesem Zeitpunkt ein so
          detailliertes messtechnisches Anforderungsprofil fuer Ladeinfrastruktur.
          Diese Grundlage blieb — mit Erweiterungen — auch nach dem
          Inkrafttreten der AFIR 2024 erhalten.
        </p>

        <DateCallout
          date="17. Maerz 2016"
          text="LSV tritt in Kraft: BNetzA-Registrierung, Steckerstandards (§ 3 LSV) und Bussgeldrahmen (§ 8 LSV) werden Pflicht."
        />
      </Section>

      {/* 4 — AFIR-Entstehung */}
      <Section
        id="afir-entstehung"
        title="2021-2023: Von der AFID zur AFIR-Verordnung"
      >
        <p>
          Im Juli 2021 veroffentlichte die Europaeische Kommission ihr
          &ldquo;Fit for 55&rdquo;-Paket. Kernbestandteil war der Vorschlag,
          die AFID durch eine unmittelbar geltende Verordnung zu ersetzen —
          die{" "}
          <strong>Alternative Fuels Infrastructure Regulation (AFIR)</strong>.
          Der Wechsel vom Richtlinien- zum Verordnungsformat bedeutete: keine
          nationalen Umsetzungsgesetze mehr notwendig, unmittelbare Geltung in
          allen 27 Mitgliedstaaten, enge Harmonisierung der Anforderungen.
        </p>
        <p>
          Die Verhandlungen zwischen Kommission, Parlament und Rat dauerten
          zwei Jahre. Strittig war insbesondere die Frage, ob Zahlungspflichten
          unmittelbar wirksam werden sollten oder Uebergangsfristen
          vorzusehen seien. Das Europaeische Parlament setzte sich fuer
          strengere Anforderungen ein, insbesondere hinsichtlich Ad-hoc-Zahlung
          und Preistransparenz.
        </p>
        <p>
          Am 22. September 2023 trat die Verordnung (EU) 2023/1804 —{" "}
          <strong>AFIR</strong> — in Kraft. Schluesselparagraphen, die fuer
          CPOs unmittelbar relevant wurden: <strong>Art. 5 AFIR</strong> zu
          Zahlungspflichten, <strong>Art. 7 AFIR</strong> zur Preistransparenz,{" "}
          <strong>Art. 10 AFIR</strong> zu Datenmeldepflichten und{" "}
          <strong>Art. 18 AFIR</strong> zu Barrierefreiheit und
          24/7-Verfuegbarkeit. Fuer die TEN-T-Abdeckung setzte Art. 3 AFIR
          konkrete Infrastrukturdichteziele mit gestaffelten Fristen.
        </p>
        <p>
          Die AFIR ist keine Erweiterung der AFID — sie ist eine strukturelle
          Neufassung. Waehrend die AFID Ziele setzte, definiert die AFIR
          Mindestanforderungen mit Bussgeldsanktionen. Die Schwelle liegt bei
          mindestens <strong>5.000 EUR pro nicht-konformem Ladepunkt pro
          Jahr</strong> nach Art. 5 AFIR, kumulativ ueber alle Verstoesse.
        </p>

        <DateCallout
          date="22. September 2023"
          text="AFIR (EU 2023/1804) tritt in Kraft — loest die AFID als unmittelbar geltende Verordnung ab."
        />
      </Section>

      {/* 5 — AFIR 2024 */}
      <Section
        id="afir-2024"
        title="2024: AFIR tritt in Kraft — was sich grundlegend aendert"
      >
        <p>
          Das Jahr 2024 markierte den Beginn der verbindlichen Umsetzungspflicht
          fuer die meisten AFIR-Anforderungen. Zum 13. April 2024 wurden die
          ersten Pflichten aus <strong>Art. 5 AFIR</strong> anwendbar: Ad-hoc-Zahlung
          ohne Vertrag, ohne App, ohne Vorregistrierung muss an jedem
          oeffentlichen Ladepunkt moeglich sein.
        </p>
        <p>
          Das bedeutete eine fundamentale Verschiebung im Geschaeftsmodell
          vieler CPOs. Wer ausschliesslich auf RFID-Karten, Roaming-Apps oder
          ISO-15118-Plug-and-Charge gesetzt hatte, musste nachruestern. Die
          AFIR-Begruendung ist eindeutig: Vertragsbasierte Zugaenge setzen
          Vorregistrierung voraus — das schliesst spontane Nutzer aus.{" "}
          <strong>Art. 5 Abs. 1 AFIR</strong> schreibt deshalb vor, dass
          Ad-hoc-Zahlung immer verfuegbar sein muss, auch wenn vertragsbasierte
          Optionen zusaetzlich angeboten werden.
        </p>
        <p>
          Ebenfalls ab 2024 wirksam: die Preistransparenzpflichten aus{" "}
          <strong>Art. 7 AFIR</strong>. Vor Ladebeginn muss der Gesamtpreis
          sichtbar sein — aufgeschluesselt nach kWh-Preis, Zeitgebuhren,
          Sitzungsgebuehren und Steuern. Eine reine In-App-Anzeige genuegt
          nicht; der Preis muss am physischen Terminal oder Stationsdisplay
          erscheinen. Fuer Ad-hoc-Nutzer ohne App ist das besonders relevant.
        </p>
        <p>
          Die kWh-Pflicht aus <strong>Art. 5 Abs. 4 AFIR</strong> verbot
          gleichzeitig minutenbasierte Abrechnung als Hauptbasis. Die
          Gesamtkosten einer Ladesitzung muessen primaer auf Kilowattstunden
          basieren. Zeitbasierte Gebuehren sind nur als sekundaere Komponente
          erlaubt — etwa als Blockiergebuehr gegen Dauerparker. Das schuf eine
          Spannung zum deutschen Eichrecht, das geeichte kWh-Messung ohnehin
          als Grundlage vorschreibt, aber in der praktischen Implementierung
          abweichende Anforderungen an Displayintervalle und Signaturen stellt.
        </p>

        <DateCallout
          date="13. April 2024"
          text="Erste AFIR-Anwendungspflichten: Ad-hoc-Zahlung (Art. 5 Abs. 1) und kWh-Primaerbasierung (Art. 5 Abs. 4) verbindlich."
        />
      </Section>

      {/* 6 — Fristen 2025 */}
      <Section
        id="fristen-2025"
        title="13. April 2025: Erster grosser Stichtag fuer CPOs"
      >
        <p>
          Der 13. April 2025 war der wichtigste Einzelstichtag der AFIR-
          Umsetzung fuer Schnellladeinfrastruktur. Ab diesem Datum ist an allen{" "}
          <strong>DC-Ladepunkten ueber 50 kW</strong>, die nach dem 13. Januar
          2025 installiert wurden, ein physisches kontaktloses Kartenterminal
          verpflichtend — nach <strong>Art. 5 Abs. 5 AFIR</strong>. QR-Code
          allein genuegt nicht. Das Terminal muss Debitkarten, Kreditkarten und
          kontaktlose Zahlungen (NFC) akzeptieren.
        </p>

        <DateCallout
          date="13. April 2025"
          text="AFIR Art. 5 Abs. 5: Physische Zahlungsterminals an allen DC-Ladepunkten ueber 50 kW verpflichtend — Debit, Kredit, NFC, kein QR-Code-Ersatz."
        />

        <p>
          Parallel dazu wurde die Datenmeldepflicht nach{" "}
          <strong>Art. 10 AFIR</strong> haerter durchgesetzt. Jeder Betreiber
          oeffentlicher Ladeinfrastruktur muss Echtzeitdaten an den National
          Access Point — in Deutschland die <strong>Mobilithek</strong> —
          liefern, im DATEX-II-Format nach <strong>Art. 13 AFIR</strong>. Die
          Anforderungen sind technisch anspruchsvoll: Verfuegbarkeitsstatus
          alle zwei Minuten, Betriebsstatus innerhalb von 30 Sekunden nach
          Aenderung, Preisaenderungen sofort, statische Daten innerhalb von 24
          Stunden nach Aenderung. Die geforderte Datenverfuegbarkeit liegt bei
          99 Prozent nach <strong>Art. 13 Abs. 5 AFIR</strong>.
        </p>
        <p>
          Das ist keine Polling-Aufgabe. Eine event-getriebene Architektur ist
          technisch notwendig, um die 30-Sekunden-Frist fuer
          Betriebsstatusaenderungen einzuhalten. Die meisten CPMS-Systeme
          koennen kein natives DATEX-II — Betreiber brauchen einen Datenadapter
          oder einen spezialisierten Dienst. Die Datenmeldung muss kostenlos,
          diskriminierungsfrei und DSGVO-konform erfolgen; personenbezogene
          Daten wie Fahrzeug-IDs und Zahlungsinformationen duerfen nicht ueber
          den NAP laufen (Art. 14 Abs. 7 AFIR).
        </p>
        <p>
          Ebenfalls 2025 konkretisierte sich die Preistransparenzpflicht aus{" "}
          <strong>Art. 7 AFIR</strong>. Die Anforderung, den Gesamtpreis vor
          Ladebeginn sichtbar anzuzeigen, schliesst dynamische Preisgestaltung
          ein: Aendern sich Preise waehrend der Sitzung, muss der aktuelle
          Preis am Terminal angezeigt und der Nutzer aktiv informiert werden.
          Fuer Roaming-Nutzer, die andere Preise als Direktkunden sehen,
          verlangt die AFIR Transparenz am Nutzungspunkt — fuer beide Gruppen.
        </p>

        <DateCallout
          date="31. Dezember 2025"
          text="AFIR Art. 13: DATEX-II-Echtzeitmeldung an die Mobilithek — alle 2 Minuten, 99 % Verfuegbarkeit, event-getrieben."
        />

        <p>
          Das Bussgeldsystem der AFIR (Art. 5) kennt keine Bagatellgrenze. Ob
          fuenf oder fuenfhundert Ladepunkte — die Pflichten sind identisch,
          und die Sanktionen kumulieren. Ein Betreiber mit 50 nicht-konformen
          Ladepunkten riskiert 250.000 EUR jaehrlich allein fuer fehlende
          Ad-hoc-Zahlung. Fehlende Preistransparenz und fehlende DATEX-II-
          Meldung kommen als separate Verstoesse hinzu.
        </p>
      </Section>

      {/* 7 — Fristen 2026 */}
      <Section
        id="fristen-2026"
        title="2026: LSV-Novelle, EnWG und V2G-Pflichten"
      >
        <p>
          Das Jahr 2026 bringt drei inhaltlich unterschiedliche
          Regulierungsebenen zusammen: die weitere LSV-Umsetzung, den neuen
          EnWG-V2G-Rahmen und die BDEW-Anforderungen fuer Netzanschluss und
          Betrieb. Fuer CPOs bedeutet das parallele Anpassungsaufgaben in
          Technik, Betrieb und Vertragsgestaltung.
        </p>
        <p>
          Die <strong>LSV</strong> schreibt in <strong>§ 4 LSV</strong> eine
          eindeutige Steckerbeschriftung vor: Typ-2- und CCS-Combo-2-Stecker
          muessen entsprechend gekennzeichnet sein. Darueber hinaus gilt{" "}
          <strong>§ 5 LSV</strong> zur Barrierefreiheit: mindestens 150 cm
          Rollstuhlabstand, Bedienelemente auf 85 bis 110 cm Hoehe,
          barrierefreie Parkplaetze mit mindestens 350 cm Breite. Zehn Prozent
          der Strassenladepunkte muessen erweiterte Barrierefreiheit bieten.
          AFIR Art. 18 ergaenzt fuer TEN-T-Stationen: 24/7-Betrieb mit
          Beleuchtung, Wetterschutz und Sicherheitseinrichtungen.
          Barrierefreiheitsmerkmale muessen als Datenpunkt an den National
          Access Point gemeldet werden (Art. 13 AFIR).
        </p>

        <DateCallout
          date="1. Maerz 2026"
          text="Erste V2G-Aggregator-Quartalsmeldung faellig: Teilnehmerzahl, Kapazitaet, geografische Verteilung, Erloese, Beschwerden (EnWG § 14c)."
        />

        <p>
          Der <strong>EnWG-V2G-Rahmen</strong> — in Kraft seit November 2025 —
          regelt in <strong>§ 14c EnWG</strong> die Voraussetzungen fuer
          Vehicle-to-Grid-Betrieb. Wer V2G koordiniert, benoetigt eine
          Aggregator-Lizenz der Bundesnetzagentur: mindestens 5 Millionen EUR
          Berufshaftpflicht pro Schadensfall, Quartalsberichte, nachgewiesene
          Cybersicherheit. EV-Besitzer sind als Prosumer eingestuft und von der
          versorgerueblichen Lizenzpflicht befreit — ohne diese Weichenstellung
          waere V2G an Compliance-Kosten gescheitert.
        </p>
        <p>
          Das EnWG verlangt ausserdem die Kopplung von V2G-Ladepunkten an
          Smart-Meter-Gateway-Infrastruktur. Bidirektionale Zaehler muessen
          eine PTB-Typzulassung haben, die den bidirektionalen Betrieb
          ausdruecklich abdeckt — unidirektionale Zulassungen genuegen nicht,
          auch wenn die Hardware die Rueckwartsmessung technisch unterstuetzt
          (EnWG Section 4). Vor jeder Entladung muss das System Vertragsstatus,
          Netzautorisierung, Ladezustand und Messgueltigkeit validieren.
        </p>
        <p>
          Die <strong>BDEW-Richtlinien</strong> sind formell nicht
          rechtsverbindlich, werden von DSOs, Versicherern und
          Foerderprogrammen aber als Voraussetzung behandelt. BDEW Section 6
          schreibt formale Inbetriebnahmepruefungen vor: Polaritaetspruefung,
          RCD-Funktionstest (Typ B), Isolationswiderstandsmessung, Spannungs-
          pruefung unter Last. Ohne diesen Nachweis verweigert der DSO den
          Netzanschluss. Fuer DC-Schnelllader ueber 22 kW beginnt das
          Netzanschlussverfahren (BDEW Section 1 und 2) mit einem formalen
          Antrag und einer Netzstudie — der realistische Vorlauf betraegt sechs
          Monate.
        </p>

        <DateCallout
          date="31. Dezember 2027"
          text="TEN-T-Gesamtnetz: DC-Schnelllader alle 60 km mit mindestens 150 kW — Planungsvorlauf beginnt jetzt (AFIR Art. 3)."
        />
      </Section>

      {/* 8 — CRA 2027 */}
      <Section
        id="cra-2027"
        title="2027: Cyber Resilience Act greift fuer Ladepunkte"
      >
        <p>
          Am <strong>11. Dezember 2027</strong> wird der{" "}
          <strong>Cyber Resilience Act</strong> (CRA, EU 2024/2847) fuer alle
          vernetzten Ladepunkte, die neu auf den EU-Markt kommen, vollstaendig
          anwendbar. Kein CE-Zeichen ohne CRA-Konformitaet. Keine Marktplatzierung
          ohne nachgewiesene Cybersicherheit nach <strong>CRA Art. 2</strong>.
          Fuer Betreiber bedeutet das: Geraete, die bis Ende 2027 installiert
          werden, muessen CRA-ready sein — bei typischen Infrastruktur-
          Lebenszyklen von zehn bis fuenfzehn Jahren ein zentrales
          Planungskriterium.
        </p>

        <DateCallout
          date="11. Dezember 2027"
          text="CRA tritt vollstaendig in Kraft: CE-Kennzeichnungspflicht fuer alle vernetzten EVSE — kein Marktzugang ohne Konformitaetsbewertung."
        />

        <p>
          Die Vulnerability-Disclosure-Pflicht nach{" "}
          <strong>CRA Art. 13</strong> ist eine der haertesten Anforderungen:
          Aktiv ausgenutzte Schwachstellen muessen innerhalb von{" "}
          <strong>24 Stunden</strong> an ENISA gemeldet werden. Die
          Remediation-Fristen sind CVSS-basiert: kritische Schwachstellen
          (CVSS 9,0-10,0) innerhalb von 14 Tagen, hohe Schwachstellen
          (CVSS 7,0-8,9) innerhalb von 30 Tagen, mittlere Schwachstellen
          (CVSS 4,0-6,9) innerhalb von 90 Tagen. Bei Vorfaellen mit mehr als
          500 betroffenen Nutzern ist sofortige Benachrichtigung Pflicht;
          netzrelevante EVSE-Vorfaelle muessen mit DSO und TSO koordiniert
          werden (CRA Section 10).
        </p>
        <p>
          Voraussetzung fuer funktionierende Vulnerability Disclosure ist eine
          vollstaendige{" "}
          <strong>Software Bill of Materials (SBOM)</strong> nach{" "}
          <strong>CRA Art. 13 Abs. 5 (Section 5 CRA)</strong>. Die SBOM muss
          alle Softwarekomponenten mit Versionsnummern, bekannte CVEs pro
          Komponente, Lieferanteninformationen und Open-Source-Lizenzen
          dokumentieren. Ohne aktuelle SBOM weiss ein Betreiber nicht einmal,
          ob er von einer neu entdeckten CVE betroffen ist — und die
          Remediation-Uhr laeuft dennoch.
        </p>
        <p>
          Das OTA-Update-System nach <strong>CRA Section 4</strong> schreibt
          Code-Signing-Verifizierung vor jeder Installation vor, Secure Boot
          mit Fail-Safe-Bootloader, Rollback-Moeglichkeit bei fehlgeschlagenem
          Update, Downgrade-Schutz gegen Rueckkehr zu verwundbaren Versionen
          und automatische Anwendung kritischer Sicherheitsupdates.
          Nicht-kritische Updates duerfen maximal 30 Tage aufgeschoben werden.
          Der Sicherheitsupdate-Support muss mindestens fuenf Jahre ab
          Marktplatzierung gewaehrleistet sein.
        </p>
        <p>
          Eine wichtige Wechselwirkung besteht mit dem deutschen Eichrecht:
          PTB-Typzulassungen sind an spezifische Firmware-Versionen gebunden
          (MessEG Section 3). OTA-Updates, die die Messfirmware beruehren,
          erfordern eine neue Typzulassung. Mess-Firmware und
          Anwendungsfirmware muessen architektonisch getrennt sein —{" "}
          eine Entscheidung, die in der Geraeteentwicklung frueh fallen muss.
          Die Produktklassifizierung nach CRA unterscheidet Klasse I
          (Standard-AC/DC-Lader, Selbstbewertung) und Klasse II (Ladehubs
          ueber 50 Fahrzeuge pro Tag, Autobahn- und Cloud-verbundene
          Flottensy steme, Pruefung durch benannte Stelle).
        </p>

        <DateCallout
          date="Ab Dezember 2029"
          text="CRA: Standardisierte Schnittstellen fuer automatisierte Marktueberwachu ngsabfragen werden verpflichtend."
        />

        <p>
          Die Sanktionen bei Nicht-Compliance sind die hoechsten im gesamten
          Regulierungsumfeld: bis zu <strong>15 Millionen EUR</strong> oder
          2,5 Prozent des weltweiten Jahresumsatzes — je nachdem, was hoeher
          ist. Technische Dokumentation muss zehn Jahre nach dem letzten in
          Verkehr gebrachten Produkt aufbewahrt werden (CRA Section 6).
        </p>
      </Section>

      {/* 9 — Ausblick 2030 */}
      <Section
        id="ausblick-2030"
        title="2030 und darueber hinaus: AFIR-Dichteplaene und Zukunft"
      >
        <p>
          Die AFIR definiert in <strong>Art. 3</strong> konkrete
          Infrastrukturdichteziele fuer das transeuropaeische Verkehrsnetz
          (TEN-T). Fuer das TEN-T-Kernnetz gilt: bis zum{" "}
          <strong>31. Dezember 2025</strong> mindestens eine
          Schnellladestation pro 60 km auf dem gesamten Kernnetz, mit
          mindestens 150 kW pro Station. Fuer das TEN-T-Gesamtnetz — ein
          breiteres Netz sekundaerer europaeischer Verbindungsachsen — laeuft
          die Frist bis zum <strong>31. Dezember 2027</strong>.
        </p>

        <DateCallout
          date="31. Dezember 2025 / 2027"
          text="AFIR Art. 3: TEN-T-Kernnetz bis Ende 2025, TEN-T-Gesamtnetz bis Ende 2027 — DC-Schnelllader alle 60 km mit mindestens 150 kW."
        />

        <p>
          Bis 2030 sieht die AFIR weitere Verschaerfungen vor. Die
          Mindestleistung pro Station am TEN-T-Kernnetz steigt auf 400 kW,
          die ISO-15118-Pflicht (Plug-and-Charge und bidirektionale
          Kommunikation) wird fuer neue DC-Saeulen ueber 50 kW verbindlich.
          Gleichzeitig laeuft der Fuenf-Jahres-V2G-Betriebszeitraum aus den
          EnWG-Foerderprogrammen ab — fruehester Foerderrueckforderungstermin:
          November 2030.
        </p>
        <p>
          Die strukturelle Herausforderung bis 2030 ist nicht technischer,
          sondern organisatorischer Natur. Sieben Regelwerke mit
          unterschiedlichen Behoerdenzustaendigkeiten (BNetzA, PTB, BSI,
          ENISA, Bundeskartellamt), unterschiedlichen Meldefristen (30
          Sekunden bis jaehrlich) und unterschiedlichen Sanktionsrahmen
          (5.000 EUR bis 15 Millionen EUR) lassen sich nicht mit
          Tabellenkalkulation steuern. Compliance-Management wird zur
          Kernkompetenz fuer jeden CPO — unabhaengig von der
          Flottengroesse.
        </p>
        <p>
          Die DSGVO-Dimension nimmt dabei zu: Ladedaten sind personenbezogen.
          Jede Ladesitzung erzeugt Standortdaten, Zeitstempel und
          Verbrauchsprofile, die Einwilligungsmanagement, Loeschrechte und
          eine 72-Stunden-Meldepflicht bei Datenschutzverstoessen ausloesen.
          Die Verknuepfung von Ladesitzungs- und Fahrzeugdaten — insbesondere
          im V2G-Kontext — erfordert datenschutzrechtliche Einwilligungen, die
          ueber einfache Cookie-Banner weit hinausgehen.
        </p>
        <p>
          Fuer CPOs, die heute planen, bedeutet das: Jede Kaufentscheidung
          fuer Hardware ist eine Compliance-Entscheidung. Jede
          Softwarearchitektur muss DATEX-II, OCMF-Signierung, CRA-OTA und
          V2G-Validierung mitdenken. Kein Hersteller-CE-Zeichen entbindet den
          Betreiber von seiner Eigenverantwortung — die regulatorische Pflicht
          liegt beim CPO, ohne Ausnahme.
        </p>

        <DateCallout
          date="November 2030"
          text="Fruehester Foerderrueckforderungstermin fuer V2G-Anlagen, die nicht fuenf Jahre V2G-Betrieb nachweisen (EnWG-V2G-Rahmen)."
        />

        <p>
          ChargeSuite wurde entwickelt, um genau diese Komplexitaet zu
          bewaeltigen. Unser Compliance Brain prueft automatisch{" "}
          <strong>188 Einzelanforderungen</strong> aus sieben Verordnungen,
          ueberwacht 20 regulatorische Deadlines und meldet Betreiber
          fristgerecht — bevor Behoerden es tun.
        </p>
        <p>
          <a
            href="/audit"
            style={{ color: "var(--color-primary)" }}
            className="font-semibold hover:underline"
          >
            Jetzt kostenlose Compliance-Pruefung starten
          </a>{" "}
          &mdash; ChargeSuite prueft 188 Anforderungen aus 7 Verordnungen
          automatisch und meldet Sie fristgerecht.
        </p>
      </Section>
    </LegalPageLayout>
  );
}
